import { useEffect, useState } from "react";
import { api, ApiError, type AvatarSummary, type Me } from "./api.ts";
import { Marketplace } from "./Marketplace.tsx";

/* --------------------------------- login ---------------------------------- */

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/login", { body: { email, password } });
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form onSubmit={submit} className="panel login-card">
        <h1>Delegate</h1>
        <p className="muted">Sign in to talk to your avatars.</p>
        <input
          type="email"
          autoComplete="username"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          autoComplete="current-password"
          placeholder="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <div className="error">⚠ {error}</div>}
        <button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------- avatars --------------------------------- */

export function AvatarBar({
  me,
  activeId,
  onSelect,
  onChanged,
  disabled,
}: {
  me: Me;
  activeId: string | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
  disabled: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [hiring, setHiring] = useState(false);

  const remove = async (a: AvatarSummary) => {
    // Deleting takes that avatar's whole context with it, which is not obvious
    // from a trash icon, so say exactly what goes.
    const ok = confirm(
      `Delete "${a.name}"?\n\nIts tasks, topics and conversations are deleted with it. ` +
        `Facts about you stay, because they belong to your account rather than to one avatar.`,
    );
    if (!ok) return;
    await api(`/api/avatars/${a.id}`, { method: "DELETE" });
    onChanged();
  };

  return (
    <div className="avatarbar">
      <span className="tenant" title={`Tenant: ${me.tenant.name}`}>
        {me.tenant.name}
        {me.tenant.hasOwnKey && <span className="badge" title="Has its own OpenAI project">own key</span>}
      </span>

      <div className="avatars">
        {me.avatars.map((a) => (
          <span key={a.id} className={`chip ${a.id === activeId ? "on" : ""}`}>
            <button className="chip-main" disabled={disabled} onClick={() => onSelect(a.id)} title={a.role}>
              {a.name}
            </button>
            {me.avatars.length > 1 && (
              <button className="chip-x" disabled={disabled} onClick={() => void remove(a)} title="Delete avatar">
                ×
              </button>
            )}
          </span>
        ))}
        <button className="chip-add" disabled={disabled} onClick={() => setHiring(true)}>
          + from marketplace
        </button>
        <button className="chip-add" disabled={disabled} onClick={() => setCreating(true)}>
          + custom
        </button>
      </div>

      <button
        className="ghost signout"
        onClick={async () => {
          await api("/api/auth/logout", { body: {} });
          location.reload();
        }}
      >
        Sign out
      </button>

      {hiring && (
        <Marketplace
          onClose={() => setHiring(false)}
          onHired={(id) => {
            setHiring(false);
            onChanged();
            onSelect(id);
          }}
        />
      )}
      {creating && (
        <NewAvatar
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onChanged();
            onSelect(id);
          }}
        />
      )}
    </div>
  );
}

function NewAvatar({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [voices, setVoices] = useState<{ voices: string[]; recommendedVoices: string[] }>({
    voices: ["marin"],
    recommendedVoices: ["marin"],
  });
  useEffect(() => {
    // The server owns the list; hardcoding it here is how it drifted to four.
    api<{ voices: string[]; recommendedVoices: string[] }>("/api/options")
      .then(setVoices)
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    name: "",
    role: "",
    responsibility: "",
    voice: "marin",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { avatar } = await api<{ avatar: AvatarSummary }>("/api/avatars", { body: form });
      onCreated(avatar.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not create avatar");
      setBusy(false);
    }
  };

  return (
    <div className="modal" onClick={onClose}>
      <form className="panel modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>New avatar</h2>
        <p className="muted">
          Starts with no memory of its own. Tasks and conversations are separate from your other
          avatars; facts about you are shared across them.
        </p>
        <label>
          Name
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Scout" required autoFocus />
        </label>
        <label>
          Role
          <input
            value={form.role}
            onChange={(e) => set("role", e.target.value)}
            placeholder="research delegate"
          />
        </label>
        <label>
          What it is responsible for
          <textarea
            rows={3}
            value={form.responsibility}
            onChange={(e) => set("responsibility", e.target.value)}
            placeholder="Find and summarise source material, and keep a reading list."
          />
        </label>
        <label>
          Voice
          <select value={form.voice} onChange={(e) => set("voice", e.target.value)}>
            {voices.voices.map((v) => (
              <option key={v} value={v}>
                {v}
                {voices.recommendedVoices.includes(v) ? "  (recommended)" : ""}
              </option>
            ))}
          </select>
        </label>
        {error && <div className="error">⚠ {error}</div>}
        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------- tenants --------------------------------- */

export function TenantAdmin({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", provisionOpenAi: true });
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await api("/api/tenants", { body: form }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not create tenant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" onClick={onClose}>
      <form className="panel modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>New tenant</h2>
        <p className="muted">
          Creates an isolated account with its own owner and first avatar. With provisioning on, it
          also gets its own OpenAI project and key, so its spend is measured separately.
        </p>
        <label>
          Organisation name
          <input value={form.name} onChange={(e) => set("name", e.target.value)} required autoFocus />
        </label>
        <label>
          Owner email
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
        </label>
        <label>
          Owner password
          <input
            type="password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            required
            minLength={8}
          />
        </label>
        <label className="row">
          <input
            type="checkbox"
            checked={form.provisionOpenAi}
            onChange={(e) => set("provisionOpenAi", e.target.checked)}
          />
          Create its own OpenAI project and key
        </label>
        {error && <div className="error">⚠ {error}</div>}
        {result && (
          <div className="result">
            <div>
              ✅ <strong>{result.tenant.name}</strong> created
            </div>
            <div className="muted">owner: {result.user.email}</div>
            <div className="muted">avatar: {result.avatar.name}</div>
            <div className="muted">
              openai: {result.provisioning?.projectId ?? result.provisioning?.error ?? "skipped"}
            </div>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            {result ? "Done" : "Cancel"}
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create tenant"}
          </button>
        </div>
      </form>
    </div>
  );
}
